import type { AdjustmentMode, SpecType } from '@local/lib';

import { getAllowedFormulaTypes, ProductPriceBasisType, QualityFormulaType, TierMode } from '@local/lib';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Collapse,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { FileSignature, Plus, Ruler, Settings, Tag, TrendingUp } from 'lucide-react';
import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import type {
  ProductPriceBasisState,
  RangeFormulaRow,
  SingleValueFormulaRow,
  SpecState,
  TemplateSpecConfig,
  ThresholdFormulaRow,
} from '../qualityFormulaTypes';

import { FieldHelpTooltip } from '../../../components/FieldHelpTooltip';
import { toNumberOrDefault } from '../numberInputValue';
import { pluralize } from '../pluralize';
import {
  CONTRACT_PRICE_COMPONENTS,
  FIELD_HELP,
  FORMULA_TYPES,
  getSpecEffectCopy,
  isMultiRowFormulaType,
  SINGLE_ROW_CONFIG_BY_FORMULA_TYPE,
  TIER_MODES,
} from '../qualityFormulaMetadata';
import { getRangeBoundaryIssues, getRangeGapInfo, validateContractedSpec } from '../qualityFormulaValidation';
import { useQualityFormulaStore } from '../store/QualityFormulaStoreProvider';
import { FieldLabelWithHelp } from './FieldLabelWithHelp';
import { RangeRowsTable } from './FormulaRowFields';
import {
  DeemedPriceField,
  ProductPriceBasisDetails,
  ProductPriceBasisField,
  type ProductPriceBasisSubFieldProps,
} from './ProductPriceBasisSection';
import { SpecSectionPanel } from './SpecSectionPanel';
import { ThresholdAppliesToControl, ThresholdRowFields } from './ThresholdRowFields';
import { formatCurrency } from './utils/resultFormatting';

type SingleRowConfig = (typeof SINGLE_ROW_CONFIG_BY_FORMULA_TYPE)[keyof typeof SINGLE_ROW_CONFIG_BY_FORMULA_TYPE];

// Kept as standalone functions (rather than inlined in QualitySpecCard) purely so their own
// ternaries/optional-chaining don't add to that component's cyclomatic complexity.
const getTierCountLabel = (tierMode: TierMode, rowCount: number): string =>
  tierMode === TierMode.Threshold ? pluralize(rowCount, 'side') : pluralize(rowCount, 'range');

const getActiveFormulaLabel = (formulaType: QualityFormulaType, fallbackLabel: string): string =>
  FORMULA_TYPES.find(option => option.value === formulaType)?.label ?? fallbackLabel;

// Supplied by the page (e.g. QualityFormulasPage), not imported here, so this quality-formulas
// feature component never has to depend on the Templates feature — `onApply` lets the rendered
// control (Templates' own LoadTemplateControl) hand back a loaded config without needing to know
// this card's store exists.
type RenderLoadTemplateControl = (
  specType: SpecType,
  specLabel: string,
  onApply: (config: TemplateSpecConfig) => void,
) => React.ReactNode;

interface SpecCardHeaderProps {
  activeFormulaLabel: string;
  /** Set only when the spec's Price Basis is Deemed Price — see showsDeemedPriceField in
   * QualitySpecCard. */
  deemedPriceBadgeLabel?: string;
  effectCopy: { aboveMeaning: string; belowMeaning: string };
  hasTieredRows: boolean;
  hint: string;
  icon: string;
  isCollapsedSummary: boolean;
  /** Set to `false` by the Template Editor, where there's only ever one card and it must stay
   * expanded — hides the collapse affordance entirely rather than just disabling the click. */
  isCollapsible: boolean;
  onApplyTemplate: (config: TemplateSpecConfig) => void;
  onToggleCollapse: (specType: SpecType) => void;
  /** One independent badge per selected component (name + price), set only when the spec's Price
   * Basis is Price Components — see showsPriceComponentsPanel in QualitySpecCard. */
  priceComponentBadgeLabels?: string[];
  renderLoadTemplateControl?: RenderLoadTemplateControl;
  specLabel: string;
  specType: SpecType;
  tierCountLabel: string;
}

// Extracted so the collapse header markup (with its own nested conditionals for the collapsed
// summary) doesn't add to the parent component's cyclomatic complexity. Load Template sits
// outside the collapse-toggle UnstyledButton (a sibling, not a descendant) so clicking it never
// also toggles the card collapsed/expanded, and is hidden (via CSS, not unmounted) while
// collapsed — unmounting it would reset its selected-template state every time the card collapses.
const SpecCardHeader: React.FC<SpecCardHeaderProps> = ({
  activeFormulaLabel,
  deemedPriceBadgeLabel,
  effectCopy,
  hasTieredRows,
  hint,
  icon,
  isCollapsedSummary,
  isCollapsible,
  onApplyTemplate,
  onToggleCollapse,
  priceComponentBadgeLabels,
  renderLoadTemplateControl,
  specLabel,
  specType,
  tierCountLabel,
}) => (
  <Group align="center" gap="sm" justify="space-between" wrap="nowrap">
    <UnstyledButton
      onClick={
        isCollapsible
          ? () => {
              onToggleCollapse(specType);
            }
          : undefined
      }
      style={{ cursor: isCollapsible ? 'pointer' : 'default', flex: 1, minWidth: 0 }}
    >
      <Group align="center" gap="sm" wrap="nowrap">
        <ThemeIcon color="blue" radius="md" size={40} variant="light">
          <Text fz={18}>{icon}</Text>
        </ThemeIcon>
        <Stack gap={2}>
          <Group gap={4} wrap="nowrap">
            <Title order={2} size="h5">
              {specLabel}
            </Title>
            <FieldHelpTooltip
              label={
                <>
                  Above contracted value = <strong>{effectCopy.aboveMeaning}</strong> &nbsp;|&nbsp; Below contracted
                  value = <strong>{effectCopy.belowMeaning}</strong>
                </>
              }
            />
          </Group>
          <Text c="dimmed" fz="xs">
            {hint}
          </Text>
          {isCollapsedSummary ? (
            <Group gap={6} mt={2}>
              <Text c="dimmed" fz="xs">
                {activeFormulaLabel}
              </Text>
              {hasTieredRows ? (
                <Badge color="blue" size="xs" variant="light">
                  {tierCountLabel}
                </Badge>
              ) : null}
              {priceComponentBadgeLabels?.map(label => (
                <Badge color="grape" key={label} size="xs" variant="light">
                  {label}
                </Badge>
              ))}
              {deemedPriceBadgeLabel ? (
                <Badge color="teal" size="xs" variant="light">
                  {deemedPriceBadgeLabel}
                </Badge>
              ) : null}
            </Group>
          ) : null}
        </Stack>
      </Group>
    </UnstyledButton>
    {renderLoadTemplateControl ? (
      <Box style={{ display: isCollapsedSummary ? 'none' : undefined, flexShrink: 0 }}>
        {renderLoadTemplateControl(specType, specLabel, onApplyTemplate)}
      </Box>
    ) : null}
  </Group>
);

interface SpecCardRangeRowsProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

// Extracted so the range list + gap callouts don't add to the parent component's complexity. The
// "+ Add Range" trigger itself is rendered by the caller as the enclosing SpecSectionPanel's
// header action (see QualitySpecCard), not here, so it sits in the same place every panel's
// header action does.
const SpecCardRangeRows: React.FC<SpecCardRangeRowsProps> = ({
  formulaType,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => {
  const gapInfo = getRangeGapInfo(rows);
  const boundaryIssues = getRangeBoundaryIssues(rows);
  const overlapMessages = boundaryIssues.filter(issue => issue.type === 'overlap').map(issue => issue.message);
  const internalGapMessages = boundaryIssues.filter(issue => issue.type === 'gap').map(issue => issue.message);

  return (
    <>
      {overlapMessages.length > 0 ? (
        <Alert color="red" variant="light">
          {overlapMessages.map(message => (
            <Text fz="sm" key={message}>
              {message}
            </Text>
          ))}
        </Alert>
      ) : null}
      {(gapInfo.belowFirstRange ?? gapInfo.aboveLastRange) !== null || internalGapMessages.length > 0 ? (
        <Alert color="yellow" variant="light">
          {gapInfo.belowFirstRange ? <Text fz="sm">{gapInfo.belowFirstRange}</Text> : null}
          {internalGapMessages.map(message => (
            <Text fz="sm" key={message}>
              {message}
            </Text>
          ))}
          {gapInfo.aboveLastRange ? <Text fz="sm">{gapInfo.aboveLastRange}</Text> : null}
        </Alert>
      ) : null}
      <RangeRowsTable
        formulaType={formulaType}
        onRemoveRow={onRemoveRow}
        onRowChange={onRowChange}
        rows={rows}
        specType={specType}
      />
    </>
  );
};

interface AppliesToPanelProps {
  onThresholdSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  rows: ThresholdFormulaRow[];
  specType: SpecType;
}

// "Applies to" — the Discount/Premium chip selector in its own panel, shown for every Threshold
// spec regardless of Price Basis (see QualitySpecCard), so it always reads as a standalone step.
const AppliesToPanel: React.FC<AppliesToPanelProps> = ({ onThresholdSideToggle, rows, specType }) => (
  <SpecSectionPanel
    description="Choose which side of the contract this rule affects."
    icon={Tag}
    title="Applies to"
    tooltip={FIELD_HELP.adjustmentMode}
  >
    <ThresholdAppliesToControl onSideToggle={onThresholdSideToggle} rows={rows} specType={specType} />
  </SpecSectionPanel>
);

interface AdjustmentBehaviorPanelProps {
  contractedSpec: number;
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof ThresholdFormulaRow,
    value: boolean | number | string,
  ) => void;
  rows: ThresholdFormulaRow[];
  /** See ThresholdRowFields' own doc comment — only passed `true` where the panel has the full
   * card width to itself (Contract Price/Deemed Price), not beside Price Components. */
  sideBySide?: boolean;
  specType: SpecType;
}

// "Adjustment Behavior" — the per-side rate cards in their own panel, separate from "Applies to"
// so each reads as its own step (see Designer references this card's layout follows).
const AdjustmentBehaviorPanel: React.FC<AdjustmentBehaviorPanelProps> = ({
  contractedSpec,
  formulaType,
  onRowChange,
  rows,
  sideBySide,
  specType,
}) => (
  <SpecSectionPanel
    description="Set the rate each side applies once it matches."
    icon={TrendingUp}
    title="Adjustment Behavior"
  >
    <ThresholdRowFields
      contractedSpec={contractedSpec}
      formulaType={formulaType}
      onRowChange={onRowChange}
      rows={rows}
      sideBySide={sideBySide}
      specType={specType}
    />
  </SpecSectionPanel>
);

interface PriceAdjustmentRangesPanelProps {
  formulaType: QualityFormulaType.Percentage | QualityFormulaType.PerUnit;
  onAddRow: (specType: SpecType) => void;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  rows: RangeFormulaRow[];
  specType: SpecType;
}

// RANGE tier mode's full-width ranges table — the table's own columns already need the full card
// width, so (unlike Threshold's Applies to/Adjustment Behavior) this never shares a row.
const PriceAdjustmentRangesPanel: React.FC<PriceAdjustmentRangesPanelProps> = ({
  formulaType,
  onAddRow,
  onRemoveRow,
  onRowChange,
  rows,
  specType,
}) => (
  <SpecSectionPanel
    action={
      <Button
        leftSection={<Plus size={14} />}
        onClick={() => {
          onAddRow(specType);
        }}
        size="xs"
        variant="outline"
      >
        Add Range
      </Button>
    }
    description="Define the adjustment for each value range."
    icon={Ruler}
    title="Price Adjustment Ranges"
  >
    <SpecCardRangeRows
      formulaType={formulaType}
      onRemoveRow={onRemoveRow}
      onRowChange={onRowChange}
      rows={rows}
      specType={specType}
    />
  </SpecSectionPanel>
);

interface SpecCardStackedLayoutProps {
  configurationPanel: React.ReactNode;
  contractedValuePanel: React.ReactNode;
  formulaType: QualityFormulaType;
  isThreshold: boolean;
  onAddRow: (specType: SpecType) => void;
  onPriceBasisChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  onRemoveRow: (specType: SpecType, rowIndex: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof RangeFormulaRow | keyof ThresholdFormulaRow,
    value: boolean | null | number | string,
  ) => void;
  onThresholdSideToggle: (specType: SpecType, adjustmentMode: AdjustmentMode, isVisible: boolean) => void;
  priceBasis: ProductPriceBasisState;
  rows: Array<RangeFormulaRow | ThresholdFormulaRow>;
  showsPriceComponentsPanel: boolean;
  specType: SpecType;
  state: SpecState;
}

// Every Price Basis except Price Components + Threshold (see showsPriceComponentsSplitLayout in
// QualitySpecCard) stacks its panels full-width, one after another, per the Contract Price/Deemed
// Price Designer reference — except Contracted Value and Applies to, which share a row (via
// `Group grow`) in Threshold mode since there's no per-side split competing for that width (unlike
// Range's full-width table). Price Components and Price Adjustment Ranges are individually
// collapsible (see SpecSectionPanel's own doc comment) rather than rearranged into their own row
// when both are shown (Range + Price Components) — that kept squeezing both tables narrower than
// they need. Extracted so that branching doesn't add to the parent component's own complexity.
// Re-derives `hasTieredRows` from `formulaType` itself (rather than taking it as a prop) so
// TypeScript's aliased-condition narrowing still applies to the panels below — that narrowing
// doesn't carry over a boolean computed in a different component.
const SpecCardStackedLayout: React.FC<SpecCardStackedLayoutProps> = ({
  configurationPanel,
  contractedValuePanel,
  formulaType,
  isThreshold,
  onAddRow,
  onPriceBasisChange,
  onRemoveRow,
  onRowChange,
  onThresholdSideToggle,
  priceBasis,
  rows,
  showsPriceComponentsPanel,
  specType,
  state,
}) => {
  const hasTieredRows = isMultiRowFormulaType(formulaType);

  if (hasTieredRows && isThreshold) {
    return (
      <Stack gap="xs">
        {configurationPanel}
        <Group align="stretch" gap="xs" grow wrap="wrap">
          {contractedValuePanel}
          <AppliesToPanel onThresholdSideToggle={onThresholdSideToggle} rows={rows} specType={specType} />
        </Group>
        <AdjustmentBehaviorPanel
          contractedSpec={state.contractedSpec}
          formulaType={formulaType}
          onRowChange={onRowChange}
          rows={rows}
          sideBySide
          specType={specType}
        />
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      {configurationPanel}
      {/* Range's Price Components has no split layout to live in (see
          showsPriceComponentsSplitLayout in QualitySpecCard, which only covers Threshold), so it
          sits right after Configuration instead of trailing after Price Adjustment Ranges. Only
          here (never Threshold's split layout) is it collapsible, since it already gets its own
          column there — see ProductPriceBasisDetails' own doc comment. */}
      {showsPriceComponentsPanel ? (
        <ProductPriceBasisDetails
          collapsible
          onChange={onPriceBasisChange}
          priceBasis={priceBasis}
          specType={specType}
        />
      ) : null}
      {contractedValuePanel}
      {hasTieredRows ? (
        <PriceAdjustmentRangesPanel
          formulaType={formulaType}
          onAddRow={onAddRow}
          onRemoveRow={onRemoveRow}
          onRowChange={onRowChange}
          rows={rows as RangeFormulaRow[]}
          specType={specType}
        />
      ) : null}
    </Stack>
  );
};

interface SpecCardTierModeControlProps {
  onChange: (specType: SpecType, tierMode: TierMode) => void;
  specType: SpecType;
  tierMode: TierMode;
}

// Segmented Threshold/Range switch shown above the tiered configuration — the single decision
// point that determines how many inputs the user has to fill in below. Carries its own
// FieldLabelWithHelp (matching Formula Type/Decimal Precision alongside it) so all three columns
// in that row start their control at the same height instead of this one sitting flush at the top.
const SpecCardTierModeControl: React.FC<SpecCardTierModeControlProps> = ({ onChange, specType, tierMode }) => (
  <Stack flex="1 1 160px" gap={4}>
    <FieldLabelWithHelp label="Tier Mode" tooltip={FIELD_HELP.tierMode} />
    <SegmentedControl
      data={TIER_MODES.map(mode => ({ label: mode.label, value: mode.value }))}
      onChange={value => {
        // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
        onChange(specType, value as TierMode);
      }}
      value={tierMode}
    />
  </Stack>
);

interface SpecCardSingleRowConfigProps {
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof SingleValueFormulaRow,
    value: number | string,
  ) => void;
  singleRowConfig: SingleRowConfig;
  specType: SpecType;
  state: SpecState;
}

// Extracted so the non-tiered (single-value) "Contracted Value" panel doesn't add to the parent
// component's complexity. Renders the same panel as the tiered Contracted Value box (see
// SpecCardContractedValuePanel) since both represent the same concept — just backed by a
// different row field per formula type (see SINGLE_ROW_CONFIG_BY_FORMULA_TYPE).
const SpecCardSingleRowConfig: React.FC<SpecCardSingleRowConfigProps> = ({
  onRowChange,
  singleRowConfig,
  specType,
  state,
}) => {
  const row = state.rows[0] as SingleValueFormulaRow | undefined;
  return (
    <SpecSectionPanel description={singleRowConfig.hint} icon={FileSignature} title={singleRowConfig.label}>
      <NumberInput
        hideControls
        onChange={value => {
          onRowChange(specType, 0, singleRowConfig.field, toNumberOrDefault(value, singleRowConfig.fallback));
        }}
        value={row?.[singleRowConfig.field] ?? singleRowConfig.fallback}
      />
    </SpecSectionPanel>
  );
};

interface ContractedValuePanelProps {
  formulaType: QualityFormulaType;
  onContractedSpecChange: (specType: SpecType, value: number) => void;
  onRowChange: (
    specType: SpecType,
    rowIndex: number,
    field: keyof SingleValueFormulaRow,
    value: number | string,
  ) => void;
  specType: SpecType;
  state: SpecState;
}

// Contracted Value (or the single-row equivalent) on its own — "Applies to" is a sibling panel
// now (see AppliesToPanel), not bundled in here, so QualitySpecCard can place each independently.
// Re-derives `hasTieredRows` from `formulaType` itself (rather than taking it as a prop) so
// TypeScript's aliased-condition narrowing still applies to the
// `SINGLE_ROW_CONFIG_BY_FORMULA_TYPE` lookup below — that narrowing doesn't carry over a boolean
// computed in a different component.
const ContractedValuePanel: React.FC<ContractedValuePanelProps> = ({
  formulaType,
  onContractedSpecChange,
  onRowChange,
  specType,
  state,
}) => {
  const hasTieredRows = isMultiRowFormulaType(formulaType);

  // QuantityAdjustedByPercent (e.g. Nitrogen) has no contracted baseline at all — billable
  // quantity is purely the actual % of shipped quantity — so there's nothing to configure here.
  if (formulaType === QualityFormulaType.QuantityAdjustedByPercent) {
    return (
      <SpecSectionPanel
        description="This formula has no contracted baseline — billable quantity is always the actual % of shipped quantity."
        icon={FileSignature}
        title="No Contracted Value"
      >
        <Text c="dimmed" size="sm">
          Not applicable for this formula type.
        </Text>
      </SpecSectionPanel>
    );
  }

  if (!hasTieredRows) {
    return (
      <SpecCardSingleRowConfig
        onRowChange={onRowChange}
        singleRowConfig={SINGLE_ROW_CONFIG_BY_FORMULA_TYPE[formulaType]}
        specType={specType}
        state={state}
      />
    );
  }

  return (
    <SpecSectionPanel
      description="The value every tier is measured against."
      icon={FileSignature}
      title="Contracted Value"
      tooltip={FIELD_HELP.contractedValue}
    >
      <NumberInput
        error={validateContractedSpec(state.contractedSpec, specType, formulaType)}
        hideControls
        onChange={value => {
          onContractedSpecChange(specType, toNumberOrDefault(value, 0));
        }}
        value={state.contractedSpec}
      />
    </SpecSectionPanel>
  );
};

interface SpecCardPriceBasisSlotProps {
  Field: React.FC<ProductPriceBasisSubFieldProps>;
  locked?: boolean;
  onChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  priceBasis: ProductPriceBasisState;
  show: boolean;
  specType: SpecType;
}

// Extracted purely to keep the optional-render branch (Product Price Basis only applies to
// PERCENTAGE formulas) out of QualitySpecCard's own complexity. Parameterized by `Field` so the
// inline "Price Basis" select and the Deemed Price amount (its own column, only once Deemed Price
// is selected) share one slot instead of two near-identical copies — the Price Components details
// are rendered directly by SpecCardAdjustmentSection, which needs to control exactly where they sit.
const SpecCardPriceBasisFieldSlot: React.FC<SpecCardPriceBasisSlotProps> = ({
  Field,
  locked,
  onChange,
  priceBasis,
  show,
  specType,
}) => (show ? <Field disabled={locked} onChange={onChange} priceBasis={priceBasis} specType={specType} /> : null);

interface ConfigurationPanelProps {
  allowedOptions: Array<{ label: string; value: QualityFormulaType }>;
  hasTieredRows: boolean;
  onFormulaTypeChange: (specType: SpecType, formulaType: QualityFormulaType) => void;
  onPrecisionChange: (specType: SpecType, value: number) => void;
  onPriceBasisChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  onTierModeChange: (specType: SpecType, tierMode: TierMode) => void;
  // Locks the Price Basis selector to its current value — the Template Editor always keeps it at
  // Contract Price; choosing Deemed Price or Price Components is a Contract Entry-only action.
  priceBasisLocked?: boolean;
  showsDeemedPriceField: boolean;
  showsPriceBasis: boolean;
  specType: SpecType;
  state: SpecState;
}

// Formula Type/Tier Mode/Decimal Precision/Price Basis — extracted so QualitySpecCard can place
// it either beside Contracted Value (Price Components, see showsPriceComponentsSplitLayout) or
// above it full-width (every other Price Basis, per the Designer reference) without duplicating
// this markup.
const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  allowedOptions,
  hasTieredRows,
  onFormulaTypeChange,
  onPrecisionChange,
  onPriceBasisChange,
  onTierModeChange,
  priceBasisLocked,
  showsDeemedPriceField,
  showsPriceBasis,
  specType,
  state,
}) => (
  <SpecSectionPanel description="Define how this attribute impacts price." icon={Settings} title="Configuration">
    <Group align="flex-start" gap="sm" wrap="wrap">
      <Stack flex="2 1 200px" gap={4}>
        <Select
          data={allowedOptions}
          label={<FieldLabelWithHelp label="Formula Type" tooltip={FIELD_HELP.formulaType} />}
          onChange={value => {
            // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
            if (value) onFormulaTypeChange(specType, value as QualityFormulaType);
          }}
          value={state.formulaType}
        />
      </Stack>
      {hasTieredRows ? (
        <SpecCardTierModeControl onChange={onTierModeChange} specType={specType} tierMode={state.tierMode} />
      ) : null}
      <Stack flex="1 1 140px" gap={4}>
        <NumberInput
          label={<FieldLabelWithHelp label="Decimal Precision" tooltip={FIELD_HELP.precision} />}
          max={10}
          min={0}
          onChange={value => {
            onPrecisionChange(specType, Number(value) || 0);
          }}
          step={1}
          value={state.precision}
        />
      </Stack>
      <SpecCardPriceBasisFieldSlot
        Field={ProductPriceBasisField}
        locked={priceBasisLocked}
        onChange={onPriceBasisChange}
        priceBasis={state.priceBasis}
        show={showsPriceBasis}
        specType={specType}
      />
      <SpecCardPriceBasisFieldSlot
        Field={DeemedPriceField}
        onChange={onPriceBasisChange}
        priceBasis={state.priceBasis}
        show={showsDeemedPriceField}
        specType={specType}
      />
    </Group>
  </SpecSectionPanel>
);

interface QualitySpecCardProps {
  /** Defaults to `true`; set to `false` by the Template Editor, where there's only ever one card
   * and it must stay expanded while editing — see `SpecCardHeader`'s own doc comment. */
  collapsible?: boolean;
  hint: string;
  icon: string;
  // Locks the Price Basis selector to its current value — set by the Template Editor, where Price
  // Basis always stays Contract Price; choosing Deemed Price or Price Components is a Contract
  // Entry-only action.
  priceBasisLocked?: boolean;
  // Omitted entirely in the Template Editor — loading a template while creating/editing another
  // template doesn't make sense (that's a Contract Entry action). Rendering the control itself is
  // left to the caller (e.g. QualityFormulasPage), not imported here, so this quality-formulas
  // feature component never has to depend on the Templates feature.
  renderLoadTemplateControl?: RenderLoadTemplateControl;
  specLabel: string;
  specType: SpecType;
}

// Reads this card's own slice of `QualityFormulaStoreProvider` directly (rather than taking
// `SpecState` + a dozen handlers as props) so neither page that renders a grid of these
// (`QualityFormulasPage`, `TemplateEditorPage`) has to thread every Step 1 handler through just to
// mount a card — only genuinely page-supplied concerns (labels, icons, the Load Template slot)
// remain props.
//
// Wrapped in `memo` so a page re-render (e.g. `QualityFormulasPage` re-rendering for an unrelated
// reason) doesn't force React to reconcile every other spec's card too — since this card's own
// Zustand selector above is what actually drives its content, and its props are otherwise stable,
// `memo` lets an edit to one spec's fields skip right past every unrelated card.
const QualitySpecCardComponent: React.FC<QualitySpecCardProps> = ({
  collapsible = true,
  hint,
  icon,
  priceBasisLocked,
  renderLoadTemplateControl,
  specLabel,
  specType,
}) => {
  const state = useQualityFormulaStore(store => store.specStates[specType]);
  const {
    applyTemplateToSpec,
    onAddRow,
    onContractedSpecChange,
    onFormulaTypeChange,
    onPrecisionChange,
    onPriceBasisChange,
    onRemoveRow,
    onRowChange,
    onThresholdSideToggle,
    onTierModeChange,
    onToggleCollapse,
  } = useQualityFormulaStore(
    useShallow(store => ({
      applyTemplateToSpec: store.applyTemplateToSpec,
      onAddRow: store.handleAddRow,
      onContractedSpecChange: store.handleContractedSpecChange,
      onFormulaTypeChange: store.handleFormulaTypeChange,
      onPrecisionChange: store.handlePrecisionChange,
      onPriceBasisChange: store.handlePriceBasisChange,
      onRemoveRow: store.handleRemoveRow,
      onRowChange: store.handleRowChange,
      onThresholdSideToggle: store.handleThresholdSideToggle,
      onTierModeChange: store.handleTierModeChange,
      onToggleCollapse: store.handleToggleCollapse,
    })),
  );
  const onApplyTemplate = (config: TemplateSpecConfig): void => {
    applyTemplateToSpec(specType, config);
  };
  const effectCopy = getSpecEffectCopy(specType);
  const { hovered, ref } = useHover<HTMLDivElement>();
  const allowedOptions = FORMULA_TYPES.filter(option => getAllowedFormulaTypes(specType).includes(option.value)).map(
    option => ({ label: option.label, value: option.value }),
  );
  const { formulaType } = state;
  const hasTieredRows = isMultiRowFormulaType(formulaType);
  const isThreshold = state.tierMode === TierMode.Threshold;
  // Product Price Basis only applies to "% of Price" (PERCENTAGE) formulas — see business rule
  // in ProductPriceBasisSection's own doc comment.
  const showsPriceBasis = formulaType === QualityFormulaType.Percentage;
  const showsDeemedPriceField = showsPriceBasis && state.priceBasis.type === ProductPriceBasisType.DeemedPrice;
  const showsPriceComponentsPanel = showsPriceBasis && state.priceBasis.type === ProductPriceBasisType.PriceComponents;
  // Price Components is the one Price Basis whose Designer reference splits the card into two
  // columns (Configuration+Price Components left, Contracted Value/Applies to/Adjustment Behavior
  // right) — every other combination (including Price Components + Range, with no reference of
  // its own) stacks every panel full-width instead, per the Contract Price/Deemed Price reference.
  const showsPriceComponentsSplitLayout = showsPriceComponentsPanel && hasTieredRows && isThreshold;
  const activeFormulaLabel = getActiveFormulaLabel(state.formulaType, specLabel);
  const tierCountLabel = getTierCountLabel(state.tierMode, state.rows.length);
  const priceComponentBadgeLabels = showsPriceComponentsPanel
    ? CONTRACT_PRICE_COMPONENTS.filter(component => state.priceBasis.selectedComponentKeys.includes(component.key)).map(
        component => `${component.label}: ${formatCurrency(component.value * component.sign)}`,
      )
    : undefined;
  const deemedPriceBadgeLabel = showsDeemedPriceField
    ? `Deemed Price: ${formatCurrency(state.priceBasis.deemedPrice)}`
    : undefined;
  const isCollapsedSummary = collapsible && state.collapsed;
  // Expanded cards already show their full content, so the hover-lift effect would just jostle
  // the open form fields — only apply it while the card is collapsed.
  const isExpanded = !isCollapsedSummary;
  const liftOnHover = hovered && !isExpanded;

  const configurationPanel = (
    <ConfigurationPanel
      allowedOptions={allowedOptions}
      hasTieredRows={hasTieredRows}
      onFormulaTypeChange={onFormulaTypeChange}
      onPrecisionChange={onPrecisionChange}
      onPriceBasisChange={onPriceBasisChange}
      onTierModeChange={onTierModeChange}
      priceBasisLocked={priceBasisLocked}
      showsDeemedPriceField={showsDeemedPriceField}
      showsPriceBasis={showsPriceBasis}
      specType={specType}
      state={state}
    />
  );
  const contractedValuePanel = (
    <ContractedValuePanel
      formulaType={formulaType}
      onContractedSpecChange={onContractedSpecChange}
      onRowChange={onRowChange}
      specType={specType}
      state={state}
    />
  );

  return (
    <Card
      p="xs"
      radius="lg"
      ref={ref}
      shadow={liftOnHover ? 'md' : 'sm'}
      style={{
        borderColor: 'var(--mantine-color-blue-6)',
        transform: liftOnHover ? 'translateY(-2px)' : undefined,
        transition: 'transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease, padding 150ms ease',
      }}
      withBorder
    >
      <SpecCardHeader
        activeFormulaLabel={activeFormulaLabel}
        deemedPriceBadgeLabel={deemedPriceBadgeLabel}
        effectCopy={effectCopy}
        hasTieredRows={hasTieredRows}
        hint={hint}
        icon={icon}
        isCollapsedSummary={isCollapsedSummary}
        isCollapsible={collapsible}
        onApplyTemplate={onApplyTemplate}
        onToggleCollapse={onToggleCollapse}
        priceComponentBadgeLabels={priceComponentBadgeLabels}
        renderLoadTemplateControl={renderLoadTemplateControl}
        specLabel={specLabel}
        specType={specType}
        tierCountLabel={tierCountLabel}
      />

      <Collapse in={isExpanded}>
        <Divider mt="xs" />
        <Stack gap="xs" pt="xs">
          {showsPriceComponentsSplitLayout ? (
            // `align="stretch"` (not `flex-start`) so Price Components starts at the same height
            // as the right column, growing past it instead of clipping if it needs more room —
            // see SpecSectionPanel/PriceComponentsTable's own `fillHeight` doc comments.
            <Group align="stretch" gap="xs" wrap="wrap">
              <Box
                style={{
                  display: 'flex',
                  flex: '2 1 320px',
                  flexDirection: 'column',
                  gap: 'var(--mantine-spacing-xs)',
                }}
              >
                {configurationPanel}
                <Box style={{ flex: 1 }}>
                  <ProductPriceBasisDetails
                    fillHeight
                    onChange={onPriceBasisChange}
                    priceBasis={state.priceBasis}
                    specType={specType}
                  />
                </Box>
              </Box>
              <Box style={{ flex: '1 1 280px' }}>
                <Stack gap="xs">
                  {contractedValuePanel}
                  <AppliesToPanel
                    onThresholdSideToggle={onThresholdSideToggle}
                    rows={state.rows as ThresholdFormulaRow[]}
                    specType={specType}
                  />
                  <AdjustmentBehaviorPanel
                    contractedSpec={state.contractedSpec}
                    formulaType={QualityFormulaType.Percentage}
                    onRowChange={onRowChange}
                    rows={state.rows as ThresholdFormulaRow[]}
                    specType={specType}
                  />
                </Stack>
              </Box>
            </Group>
          ) : (
            <SpecCardStackedLayout
              configurationPanel={configurationPanel}
              contractedValuePanel={contractedValuePanel}
              formulaType={formulaType}
              isThreshold={isThreshold}
              onAddRow={onAddRow}
              onPriceBasisChange={onPriceBasisChange}
              onRemoveRow={onRemoveRow}
              onRowChange={onRowChange}
              onThresholdSideToggle={onThresholdSideToggle}
              priceBasis={state.priceBasis}
              rows={state.rows as Array<RangeFormulaRow | ThresholdFormulaRow>}
              showsPriceComponentsPanel={showsPriceComponentsPanel}
              specType={specType}
              state={state}
            />
          )}
        </Stack>
      </Collapse>
    </Card>
  );
};

export const QualitySpecCard = memo(QualitySpecCardComponent);
